/**
 * A sample program bundled with the language.
 *
 * @typedef {object} Example
 * @property {string} name
 * @property {string} code
 */

/** @type {Example[]} */
export const EXAMPLES = [
  {
    name: "Equilateral triangle (Elements I.1)",
    code: [
      "// Elements, Book I, Proposition 1 (c. 300 BC) — the very",
      "// first construction in Euclid: an equilateral triangle",
      "// on a given segment.",
      "A=point(0, 0);",
      "B=point(4, 0);",
      "c1=circle(A, B-A);",
      "c2=circle(B, A-B);",
      "C=pick P in c1.c2: P.y > 0;",
      "draw {A, B, C};",
      "",
    ].join("\n"),
  },
  {
    name: "Perpendicular bisector (Elements I.10)",
    code: [
      "// Elements I.10 — bisecting a segment with two circles.",
      "// The chord of the two crossings is the perpendicular",
      "// bisector of AB.",
      "A=point(-2, 0);",
      "B=point(2, 0);",
      "c1=circle(A, B-A);",
      "c2=circle(B, A-B);",
      "P=pick Q in c1.c2: Q.y > 0;",
      "R=pick Q in c1.c2: Q.y < 0;",
      "l=line(P, R);",
      "draw l;",
      "",
    ].join("\n"),
  },
  {
    name: "Square on a segment (Elements I.46)",
    code: [
      "// Elements I.46 — a square described on a given segment.",
      "// A perpendicular is erected at each end (I.11), and two",
      "// circles carry the length of AB around the corners.",
      "A=point(0, 0);",
      "B=point(3, 0);",
      "l=line(A, B);",
      "m=line(B, l.rot(90).inf);",
      "c1=circle(B, A-B);",
      "C=pick P in c1.m: P.y > 0;",
      "n=line(A, l.rot(90).inf);",
      "c2=circle(A, B-A);",
      "D=pick P in c2.n: P.y > 0;",
      "draw {A, B, C, D};",
      "",
    ].join("\n"),
  },
  {
    name: "Regular hexagon (Elements IV.15)",
    code: [
      "// Elements IV.15 — a regular hexagon in a circle. The side",
      "// of a hexagon equals the radius, so the same compass",
      "// opening that drew the circle steps around it exactly",
      "// six times.",
      "O=point(0, 0);",
      "A=point(3, 0);",
      "c=circle(O, A-O);",
      "c1=circle(A, O-A);",
      "B=pick P in c.c1: P.y > 0;",
      "c2=circle(B, O-B);",
      "C=pick P in c.c2: P != A;",
      "c3=circle(C, O-C);",
      "D=pick P in c.c3: P != B;",
      "c4=circle(D, O-D);",
      "E=pick P in c.c4: P != C;",
      "c5=circle(E, O-E);",
      "F=pick P in c.c5: P != D;",
      "draw {A, B, C, D, E, F};",
      "",
    ].join("\n"),
  },
  {
    name: "Tangents from a point (Elements III.17)",
    code: [
      "// Tangents to a circle from an external point, Elements",
      "// III.17. The circle on diameter OP (Thales — the angle in",
      "// a semicircle is right, III.31) meets c1 exactly where a",
      "// radius stands at a right angle, which is where a tangent",
      "// touches (III.18). Try dragging P with the select tool.",
      "O=point(0, 0);",
      "A=point(2, 0);",
      "c1=circle(O, A-O);",
      "P=point(6, 1);",
      "M=O+0.5*(P-O);",
      "c2=circle(M, O-M);",
      "T1=pick Q in c1.c2: Q.y > 0;",
      "T2=pick Q in c1.c2: Q != T1;",
      "t1=line(P, T1);",
      "t2=line(P, T2);",
      "draw c1;",
      "draw t1;",
      "draw t2;",
      "",
    ].join("\n"),
  },
  {
    name: "Pentagon (Richmond, exact)",
    code: [
      "// An exact regular pentagon, after H. W. Richmond (1893).",
      "// Where Dürer's shortcut never constructs the golden",
      "// ratio, Richmond does: W is the midpoint of a radius, and",
      "// the bisector of angle OWV cuts the axis at height",
      "// R*cos(72) — the height of the two upper vertices. Euclid",
      "// already had an exact construction (Elements IV.11);",
      "// this is the compact modern one.",
      "O=point(0, 0);",
      "V=point(0, 2);",
      "c=circle(O, V-O);",
      "l=line(O, V);",
      "h=line(O, l.rot(90).inf);",
      "E=pick P in c.h: P.x > 0;",
      "W=O+0.5*(E-O);",
      "// bisect the angle OWV: cw cuts both of its arms at the",
      "// same distance from W (through O itself on one side)",
      "cw=circle(W, O-W);",
      "Y=pick P in cw.line(W, V): P.y > 0;",
      "b=line(W, O+0.5*(Y-O));",
      "T=b.l;",
      "m=line(T, l.rot(90).inf);",
      "P2=pick P in c.m: P.x > 0;",
      "P5=pick P in c.m: P.x < 0;",
      "// the side length is set; walk it around the circle",
      "c2=circle(P2, V-P2);",
      "P3=pick P in c.c2: P != V;",
      "c3=circle(P5, V-P5);",
      "P4=pick P in c.c3: P != V;",
      "draw {V, P2, P3, P4, P5};",
      "",
    ].join("\n"),
  },
  {
    name: "Pentagon (Dürer, approximate)",
    code: [
      "// Dürer's pentagon, from his 1525 'Underweysung der",
      "// Messung', after the medieval masons' Geometria deutsch.",
      "// Built with a single compass opening (a 'rusty compass'),",
      "// it is exactly equilateral — every side is a radius of",
      "// one of the equal circles — but only nearly regular: the",
      "// base angles come out 108°22' instead of 108°, an error",
      "// far too small to draw. Dürer never mentioned it.",
      "A=point(0,0);",
      "B=point(5,0);",
      "draw line(A,B);",
      "l=line(A+0.5*(B-A),line(A,B).rot(90).inf);",
      "c1=circle(A,B-A);",
      "c2=circle(B,A-B);",
      "C=pick P in c1.c2: P.y > 0;",
      "c3=circle(C,A-C);",
      "D=pick P in c3.l: P in c2;",
      "E=pick P in c3.c1: P != B;",
      "F=pick P in c3.c2: P != A;",
      "k1=line(E,D);",
      "k2=line(F,D);",
      "G=pick P in c1.k2: not (P in c2);",
      "H=pick P in c2.k1: not (P in c1);",
      "c4=circle(H,H-B);",
      "I=pick P in c4.l: not (P in c1);",
      "draw {A,B,H,I,G};",
      "",
    ].join("\n"),
  },
  {
    name: "Blank canvas",
    code: [
      "// Pick a tool on the left and start constructing,",
      "// or type euclid code right here.",
      "",
    ].join("\n"),
  },
];
